import React from "react";
import PolicyLayout from "../PolicyLayout";
import { linkifyNBFC } from "../../utils/nbfcLink";
import { Link } from "react-router-dom";

const PrivacyPolicy: React.FC = () => {
  const policySections = [
    {
      title: "1. Information We Collect",
      list: [
        "Personal Identification: Full Name, Email, Phone Number, Date of Birth, Address, PAN, Aadhaar, Employment Information, Bank Account Details, and Financial Data.",
        "Financial Information: Documents such as Bank Statements, Salary Slips, and Consent for Credit Checks are required for loan applications.",
        "Transactional Information: Details about your loans, repayment schedules, and transaction history are recorded.",
      ],
    },
    {
      title: "2. How We Use Your Information",
      list: [
        "Loan Processing: We assess your loan eligibility, process applications, and manage repayments.",
        "Service Improvement: We enhance our platform based on user feedback and interactions.",
        "Communication: We send notifications and respond to inquiries related to our services.",
        "Compliance: We comply with legal requirements like KYC and AML regulations.",
      ],
    },
    {
      title: "3. Sharing Your Information",
      list: [
        "Authorized Third Parties: We work with Credit Bureaus, Payment Gateways, and Verification Agencies to process and verify your information.",
        "Legal Authorities: We may disclose your information as required by the law, such as for regulatory reporting or fraud investigations.",
        `Parent Company: We may share your data with our parent company, ${import.meta.env.VITE_COMPANY_NBFC}, for loan processing.`,
      ],
    },
    {
      title: "4. Data Security",
      content:
        "We employ encryption, secure servers, and multi-factor authentication to safeguard your data. However, no system is 100% secure, and we encourage users to proceed with caution.",
    },
    {
      title: "5. Cookies and Tracking",
      content:
        "We use cookies and tracking technologies to enhance your browsing experience. You can control cookies through browser settings.",
    },
    {
      title: "6. Your Rights",
      content: (
        <>
          You have the right to access, correct, delete, or request portability of
          your data. You can also opt out of marketing communications at any time.
          <br />
          <br />
          For requesting deletion of your personal data, please refer to our{" "}
          <Link
            to="/account-delete-policy"
            className="text-[var(--primary)] hover:underline font-bold"
          >
            User Data Deletion Policy
          </Link>
          .
        </>
      ),
    },
    {
      title: "7. Retention of Information",
      content:
        "We retain your information as long as it is necessary for business or legal reasons, and securely delete it when it is no longer needed.",
    },
    {
      title: "8. Third-Party Links",
      content:
        "Our platform may link to third-party websites. We are not responsible for their privacy practices.",
    },
    {
      title: "9. Children’s Privacy",
      content:
        "We do not knowingly collect data from children under 18. If we find any, we will delete it.",
    },
    {
      title: "10. Policy Updates",
      content:
        "We may update this policy periodically. Check this page for any changes.",
    },
    {
      title: "11. Contact Us",
      list: [
        `Email: ${import.meta.env.VITE_COMPANY_EMAIL}`,
        `Phone: ${import.meta.env.VITE_COMPANY_PHONE}`,
        `Address: ${import.meta.env.VITE_COMPANY_ADDRESS}`,
      ],
    },
  ];

  return (
    <PolicyLayout 
      title="Privacy Policy" 
      subtitle={`At ${import.meta.env.VITE_COMPANY_NAME}, we guard your data with bank-grade security. Your privacy is our professional commitment and institutional priority.`}
      badge="Data Protection 🔒"
    >
      {policySections.map((section, idx) => (
        <div key={idx} className="space-y-6">
          <h2 className="text-3xl font-black text-[#0F172A] tracking-tight">
            {linkifyNBFC(section.title)}
          </h2>

          {section.content && (
            <div className="text-[var(--text-secondary)] text-lg leading-relaxed font-medium whitespace-pre-line">
              {linkifyNBFC(section.content)}
            </div>
          )}
          
          {section.list && (
            <ul className="space-y-4">
              {section.list.map((li, i) => (
                <li key={i} className="flex items-start gap-3 text-[var(--text-secondary)] text-lg font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)] mt-2.5 flex-shrink-0" />
                  {linkifyNBFC(li)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </PolicyLayout>
  );
};

export default PrivacyPolicy;
